import { useQuery } from '@api/hooks'
import PageTitle from '@components/PageTitle/PageTitle'
import { Program as ProgramType } from '@domain'
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
	Alert,
	Box,
	Breadcrumbs,
	IconButton,
	Link,
	Skeleton,
	Stack,
	Typography,
} from '@mui/material'
import ProgramsApi, { ProgramsKeys } from '@services/programs.api'
import BasketHeader from '@views/MarketBaskets/components/BasketHeader/BasketHeader'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'

import { useMarketBasketContext } from '../MarketBaskets.hooks'
import ProductDetails from './components/ProductDetails'
import Program from './components/Program'

export default function Programs() {
	const { marketBasketId, similarProductId } = useParams()

	const { isLoading: mbLoading, marketBasket } = useMarketBasketContext()

	const { data, isLoading } = useQuery({
		queryKey: ProgramsKeys.basketPrograms(marketBasketId, similarProductId),
		queryFn: () => ProgramsApi.basketPrograms(marketBasketId, similarProductId),
	})

	return (
		<Box width="100%">
			<PageTitle documentTitle={mbLoading ? '' : marketBasket.displayName} title={<Title />} />

			<Stack px="2rem" spacing={2}>
				<BasketHeader>
					<BasketHeader.InfoBar />
				</BasketHeader>

				<ProductDetails />

				{isLoading ? (
					<Stack spacing={2}>
						<Skeleton variant="rectangular" height="18rem" />
						<Skeleton variant="rectangular" height="18rem" />
					</Stack>
				) : (
					<ProgramList programs={data.programs} />
				)}
			</Stack>
		</Box>
	)
}

function ProgramList({ programs }: { programs: ProgramType[] }) {
	if (programs?.length === 0) {
		return <Alert severity="warning">No programs found for this product</Alert>
	}

	return programs.map((program) => <Program key={program.programType} program={program} />)
}

function Title() {
	const navigate = useNavigate()
	const { marketBasketId, similarProductId } = useParams()
	const { isLoading, marketBasket } = useMarketBasketContext()

	return (
		<Breadcrumbs>
			<IconButton onClick={() => navigate(-1)} color="info">
				<FontAwesomeIcon icon={faChevronLeft} size="sm" />
			</IconButton>
			<Link component={RouterLink} underline="hover" color="info.light" to="/app/market-baskets">
				Market Baskets
			</Link>
			{isLoading ? (
				<Skeleton width="10rem" />
			) : (
				<Link
					component={RouterLink}
					underline="hover"
					color="info.light"
					to={`/app/market-baskets/${marketBasketId}`}
				>
					{`${marketBasket.id} - ${marketBasket.displayName}`}
				</Link>
			)}
			{isLoading ? (
				<Skeleton width="10rem" />
			) : (
				// eslint-disable-next-line jsx-a11y/anchor-is-valid
				<Link
					underline="hover"
					color="info.light"
					onClick={() => navigate(-1)}
					sx={{ cursor: 'pointer' }}
				>
					{similarProductId}
				</Link>
			)}

			<Typography color="text.primary" variant="body1">
				Programs
			</Typography>
		</Breadcrumbs>
	)
}
